import React, { useEffect, useState } from 'react';

import { useTranslation } from 'react-i18next';
import { ColocIcon } from '@/components/design-system/atoms/icon';
import { Button } from '@/components/design-system/atoms/button';
import { BroadcastMessageStatus, BroadcastMessage } from '../../types';
import { useGetBroadcastMessages } from '../../hooks/use-get-broadcast-messages';
import { LoadingSpinner } from '@/components/design-system/atoms/loading-spinner';
import { EditBroadcastSlideOver } from '../edit-broadcast-slide-over';
import { DropDown } from '@/components/design-system/molecules/dropdown';
import { MessageCard } from './message-card';
import { CreateBroadcastSlideOver } from '../create-broadcast-slide-over';
import { useColocations } from '../../hooks/use-colocations';

const BroadcastMessagesList = () => {
  const { t } = useTranslation()!;
  const [activeTab, setActiveTab] = useState<BroadcastMessageStatus>('active');
  const [editMessage, setEditMessage] = useState<BroadcastMessage | null>(null);
  const [selectedColocationIds, setSelectedColocationIds] = useState<string[]>(['all']);
  const [query, setQuery] = useState('');
  const [isAllSelected, setIsAllSelected] = useState(true);
  const [showCreateMessage, setShowCreateMessage] = useState(false);
  const { colocations, isLoading: isColocationsLoading } = useColocations();

  const {
    data: messages,
    isLoading,
    refetch,
  } = useGetBroadcastMessages({
    status: activeTab,
    colocation_ids: isAllSelected ? [] : selectedColocationIds,
  });

  const deleteColocation = (colocationId: string) => {
    const colocationIdsCopy = [...selectedColocationIds];
    setSelectedColocationIds(colocationIdsCopy.filter((value) => value !== colocationId));
  };

  const handleColocationChange = (selected: string[]) => {
    const wasAllSelected = selectedColocationIds.includes('all');
    const currentIsAllSelected = selected.includes('all');

    // If "all" was just selected, select all houses
    if (!wasAllSelected && currentIsAllSelected) {
      setSelectedColocationIds([
        'all',
        ...colocations.map((colocation) => colocation.id.toString()),
      ]);
      setIsAllSelected(true);
      return;
    }

    // If "all" was just unselected, clear all selections
    if (wasAllSelected && !currentIsAllSelected) {
      setSelectedColocationIds([]);
      setIsAllSelected(false);
      return;
    }

    // If a specific house was unselected and "all" was previously selected,
    // remove "all" and keep all other houses except the unselected one
    if (wasAllSelected && selected.length < selectedColocationIds.length) {
      const newSelected = selected.filter((item) => item !== 'all');
      setSelectedColocationIds(newSelected);
      setIsAllSelected(false);
      return;
    }

    // For all other cases, just update the selection
    setSelectedColocationIds(selected);
    setIsAllSelected(false);
  };

  useEffect(() => {
    if (colocations.length > 0) {
      setSelectedColocationIds([
        'all',
        ...colocations.map((colocation) => colocation.id.toString()),
      ]);
    }
  }, [colocations]);

  if (isLoading || isColocationsLoading) {
    return (
      <div className="flex h-full w-full items-center justify-center">
        <LoadingSpinner />
      </div>
    );
  }

  const filteredColocations =
    query === ''
      ? colocations
      : colocations.filter((colocation) => {
          return colocation.name.toLowerCase().includes(query.toLowerCase());
        });

  return (
    <div className="flex h-full flex-1 flex-col overflow-hidden px-7 pt-7">
      <div className="mb-3 flex items-center justify-between">
        <h1 className="text-h3 font-bold text-text-primary">
          {t('js.features.admin.broadcast_messages.title')}
        </h1>
        <Button
          iconName="add"
          id="create-new-message-button"
          onClick={() => setShowCreateMessage(true)}
          variant="primary"
        >
          {t('js.features.admin.broadcast_messages.create_new_broadcast_message')}
        </Button>
      </div>
      <div className="scrollbar-hide mb-4 flex h-full w-full flex-1 flex-col gap-4 overflow-scroll rounded-lg bg-white p-4 shadow-sm">
        <div className="flex items-center gap-2">
          <ColocIcon name="message" className="text-text-secondary" size={40} />
          <h1 className="text-lead1 font-bold text-E300">
            {t('js.features.admin.broadcast_messages.messages_list')}
          </h1>
        </div>

        <div className="flex justify-between gap-2">
          <div className="flex h-[36px] items-start gap-2">
            <Button
              variant={activeTab === 'active' ? 'secondary' : 'tertiary'}
              onClick={() => setActiveTab('active')}
              rounded
            >
              {t('js.features.admin.broadcast_messages.tabs.active')}
            </Button>
            <Button
              variant={activeTab === 'scheduled' ? 'secondary' : 'tertiary'}
              onClick={() => setActiveTab('scheduled')}
              rounded
            >
              {t('js.features.admin.broadcast_messages.tabs.scheduled')}
            </Button>
            <Button
              variant={activeTab === 'archived' ? 'secondary' : 'tertiary'}
              onClick={() => setActiveTab('archived')}
              rounded
            >
              {t('js.features.admin.broadcast_messages.tabs.archived')}
            </Button>
          </div>

          <DropDown
            label={t('js.features.admin.broadcast_messages.form.houses')}
            className="w-full max-w-[350px]"
            value={selectedColocationIds}
            multiple
            onChange={handleColocationChange}
            onTextInputChange={(event) => setQuery(event.target.value)}
            inputWrapperClassName="bg-white"
          >
            <DropDown.DropDownInputMultipleValues>
              {!isAllSelected &&
                selectedColocationIds
                  .filter((colocationId) => colocationId !== 'all')
                  .map((colocationId) => {
                    return (
                      <DropDown.DropDownInputMultipleValueDefaultContent
                        key={colocationId}
                        onClear={() => deleteColocation(colocationId)}
                      >
                        {colocations.find((colocation) => colocation.id.toString() === colocationId)
                          ?.name}
                      </DropDown.DropDownInputMultipleValueDefaultContent>
                    );
                  })}

              {isAllSelected && (
                <DropDown.DropDownInputMultipleValueDefaultContent>
                  {t('js.features.admin.broadcast_messages.form.all_houses')}
                </DropDown.DropDownInputMultipleValueDefaultContent>
              )}
            </DropDown.DropDownInputMultipleValues>
            <DropDown.DropDownOptions>
              <DropDown.DropDownOption value="all">
                <DropDown.DropDownOptionDefaultContent>
                  {t('js.features.admin.broadcast_messages.form.all_houses')}
                </DropDown.DropDownOptionDefaultContent>
              </DropDown.DropDownOption>
              {filteredColocations.map((colocation, index) => {
                return (
                  <DropDown.DropDownOption key={index} value={colocation.id.toString()}>
                    <DropDown.DropDownOptionDefaultContent>
                      {colocation.name}
                    </DropDown.DropDownOptionDefaultContent>
                  </DropDown.DropDownOption>
                );
              })}
            </DropDown.DropDownOptions>
          </DropDown>
        </div>

        <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
          {messages?.map((message) => (
            <MessageCard
              key={message.id}
              title={message.title}
              message={message.message_preview}
              startDate={new Date(message.starts_at)}
              status={activeTab}
              onEdit={setEditMessage}
              messageData={message}
              refetch={refetch}
            />
          ))}
        </div>

        <EditBroadcastSlideOver
          show={!!editMessage}
          onClose={() => setEditMessage(null)}
          broadcastMessage={editMessage}
          colocations={colocations}
          onSuccess={() => {
            refetch();
          }}
        />
      </div>
      <CreateBroadcastSlideOver
        show={showCreateMessage}
        colocations={colocations}
        onClose={() => setShowCreateMessage(false)}
        onSuccess={() => {
          refetch();
        }}
      />
    </div>
  );
};

export { BroadcastMessagesList };
